import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2,
  Search,
  Send,
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  MessageSquare,
  FileWarning,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  farmGet,
  farmPatch,
  farmPost,
  STATO_CONTROLLO_LABELS,
  STATO_ESSENZIALE_LABELS,
  COPERTURA_FARMACO_LABELS,
} from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Kpi({ label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={`rounded-xl border border-slate-200/80 p-4 ${tones[tone] || tones.slate}`}>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

export default function ControlloRicette({ embedded = false }) {
  const { token } = useFarmaciaAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [kpi, setKpi] = useState({});
  const [strutture, setStrutture] = useState([]);
  const [stato, setStato] = useState("");
  const [orgFilter, setOrgFilter] = useState(() => searchParams.get("organization_id") || "");
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [searchApplied, setSearchApplied] = useState(() => searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(null);
  const [azioneMsg, setAzioneMsg] = useState("");
  const [preselectedRequestDone, setPreselectedRequestDone] = useState(false);

  useEffect(() => {
    const fromUrl = searchParams.get("organization_id") || "";
    const query = searchParams.get("q") || "";
    setOrgFilter(fromUrl);
    setSearch(query);
    setSearchApplied(query);
    setPreselectedRequestDone(false);
  }, [searchParams]);

  useEffect(() => {
    if (!token) return;
    farmGet("/farmacia/strutture", token, { lite: true })
      .then((s) => setStrutture(s.items || []))
      .catch(() => setStrutture([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [data, inboundData] = await Promise.all([
        farmGet("/farmacia/ricette/controllo", token, {
          ...(stato ? { stato } : {}),
          ...(searchApplied ? { q: searchApplied } : {}),
          ...(orgFilter ? { organization_id: orgFilter } : {}),
        }),
        farmGet("/farmacia/inbound-ricette", token, { stato: "da_revisionare" }).catch(() => ({ items: [] })),
      ]);
      setItems(data.items || []);
      setKpi(data.kpi || {});
      setInbound(inboundData.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento controllo");
    } finally {
      setLoading(false);
    }
  }, [token, stato, searchApplied, orgFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    const next = search.trim();
    if (next === searchApplied) {
      load();
    } else {
      setSearchApplied(next);
    }
  };

  const toggle = (id) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const openDetail = useCallback(async (richiestaId) => {
    setLoadingDetail(true);
    try {
      const res = await farmGet(
        `/farmacia/ricette/richieste/${encodeURIComponent(richiestaId)}/controllo-dettaglio`,
        token,
      );
      setDetail(res);
      setAzioneMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore dettaglio");
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [token]);

  useEffect(() => {
    const richiestaId = searchParams.get("richiesta_id");
    if (!richiestaId || loading || loadingDetail || preselectedRequestDone) return;
    setPreselectedRequestDone(true);
    openDetail(richiestaId);
  }, [loading, loadingDetail, openDetail, preselectedRequestDone, searchParams]);

  const downloadAllegato = async (ricId, fileId, fileName) => {
    setBusy(`dl-${fileId}`);
    try {
      const res = await axios.get(
        `${API_URL}/api/farmacia/ricette/ricette-medico/${encodeURIComponent(ricId)}/allegato/${encodeURIComponent(fileId)}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "ricetta.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fallito");
    } finally {
      setBusy(null);
    }
  };

  const setCopertura = async (farmacoId, coperturaStato, ricettaId, ricettaFarmacoId) => {
    if (!detail?.richiesta?.id) return;
    setBusy(`cov-${farmacoId}`);
    try {
      const res = await farmPatch(
        `/farmacia/ricette/richieste/${encodeURIComponent(detail.richiesta.id)}/copertura`,
        token,
        {
          farmaco_id: farmacoId,
          stato: coperturaStato,
          ricetta_id: ricettaId || undefined,
          ricetta_farmaco_id: ricettaFarmacoId || undefined,
        },
      );
      toast.success(
        res.ciclo_stato === "chiusa"
          ? "Copertura aggiornata: pratica conclusa"
          : res.ciclo_stato === "riaperta"
            ? "Copertura aggiornata: pratica riaperta"
            : "Copertura aggiornata"
      );
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              stato_essenziale: res.stato_essenziale || prev.stato_essenziale,
              farmaci_richiesti: (prev.farmaci_richiesti || []).map((f) =>
                f.id === farmacoId
                  ? { ...f, copertura: coperturaStato, associazione: res.associazione }
                  : f
              ),
            }
          : prev
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setBusy(null);
    }
  };

  const setRicettaStato = async (ricId, statoEss) => {
    setBusy(`st-${ricId}`);
    try {
      await farmPatch(
        `/farmacia/ricette/ricette-medico/${encodeURIComponent(ricId)}/stato-essenziale`,
        token,
        { stato: statoEss },
      );
      toast.success("Stato ricetta aggiornato");
      await openDetail(detail.richiesta.id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setBusy(null);
    }
  };

  const inviaAzione = async (tipo) => {
    if (!detail?.richiesta?.id) return;
    setBusy(`az-${tipo}`);
    try {
      await farmPost(
        `/farmacia/ricette/richieste/${encodeURIComponent(detail.richiesta.id)}/azione`,
        token,
        {
          tipo,
          messaggio: azioneMsg || undefined,
          ricetta_id: detail.ricette?.[0]?.id,
        },
      );
      toast.success(
        tipo === "nuova_ricetta"
          ? "Richiesta nuova ricetta inviata alla struttura"
          : tipo === "documento_illeggibile"
            ? "Segnalazione illeggibile inviata"
            : "Nota inviata"
      );
      setAzioneMsg("");
      await openDetail(detail.richiesta.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Azione fallita");
    } finally {
      setBusy(null);
    }
  };

  const sollecita = async () => {
    const ids = [...checked].filter((id) => {
      const row = items.find((i) => i.richiesta_id === id);
      return row?.sollecitabile;
    });
    if (!ids.length) {
      toast.error("Seleziona almeno una richiesta sollecitabile");
      return;
    }
    setSending(true);
    try {
      const res = await farmPost("/farmacia/ricette/sollecita", token, { richiesta_ids: ids });
      toast.success(`Solleciti inviati: ${res.inviati || 0}`);
      setChecked(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sollecito fallito");
    } finally {
      setSending(false);
    }
  };

  const sollecitaUno = async (id) => {
    setSending(true);
    try {
      await farmPost("/farmacia/ricette/sollecita", token, { richiesta_ids: [id] });
      toast.success("Sollecito inviato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sollecito fallito");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome
        title="Pratiche — verifica ricette"
      >
        {checked.size > 0 ? (
          <Button
            onClick={sollecita}
            disabled={sending}
            size="sm"
            className="rounded-xl bg-amber-600 hover:bg-amber-700"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Sollecita ({checked.size})
          </Button>
        ) : null}
      </FarmaciaPageChrome>
      <div className="flex w-fit gap-1 rounded-xl bg-slate-100 p-1 text-xs">
        <Link to="/farmacia/pratiche" className="rounded-lg px-3 py-1.5 text-slate-600 hover:text-slate-900">Flusso pratiche</Link>
        <Link to="/farmacia/pratiche?vista=copertura" className={`rounded-lg px-3 py-1.5 ${embedded ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>Verifica ricette</Link>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Totale" value={kpi.totale} tone="slate" />
        <Kpi label="Complete" value={kpi.complete} tone="emerald" />
        <Kpi label="Parziali" value={kpi.parziali} tone="amber" />
        <Kpi label="Nessuna ricetta" value={kpi.nessuna_ricetta} tone="rose" />
        <Kpi label="Sollecitabili" value={kpi.sollecitabili} tone="amber" />
      </div>
      {inbound.length ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {inbound.length} ricett{inbound.length === 1 ? "a" : "e"} da struttura esterna da associare manualmente: manca un codice fiscale univoco o il paziente non è presente.
        </div>
      ) : null}
      <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-xs text-slate-600 md:grid-cols-3">
        <p><strong className="text-slate-800">Confronto automatico:</strong> verifica se i farmaci letti nelle ricette corrispondono a quelli richiesti.</p>
        <p><strong className="text-slate-800">Verifica farmacia:</strong> è l’esito operativo confermato dal farmacista, anche per singolo farmaco.</p>
        <p><strong className="text-slate-800">Farmaci non rilevati:</strong> sono presenti nella richiesta ma non trovati nelle ricette caricate.</p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
        >
          <option value="">Tutte le strutture</option>
          {strutture.map((s) => (
            <option key={s.organization_id} value={s.organization_id}>
              {s.nome || s.organization_id}
            </option>
          ))}
        </select>
        <select
          className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm"
          value={stato}
          onChange={(e) => setStato(e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          <option value="complete">Complete</option>
          <option value="parziali">Parziali</option>
          <option value="nessuna_ricetta">Nessuna ricetta</option>
          <option value="scadute">Scadute</option>
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-11 rounded-xl pl-10"
            placeholder="Cerca ospite… (Invio)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
        </div>
        <Button variant="outline" className="h-11 rounded-xl" onClick={applySearch}>
          Aggiorna
        </Button>
      </div>

      <Card className="overflow-hidden rounded-xl shadow-sm">
        <CardHeader className="border-b px-4 py-3">
          <CardTitle className="text-sm">Cicli richiesta / ricette</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Nessun ciclo da controllare</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/60 text-left text-xs uppercase text-slate-500">
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2">Ospite</th>
                    <th className="px-3 py-2" title="Esito operativo confermato dalla farmacia">Verifica farmacia</th>
                    <th className="px-3 py-2" title="Confronto automatico fra richiesta e farmaci letti nelle ricette">Confronto automatico</th>
                    <th className="px-3 py-2" title="Farmaci richiesti non trovati nelle ricette caricate">Farmaci non rilevati</th>
                    <th className="px-3 py-2">Attesa</th>
                    <th className="px-3 py-2">Solleciti</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const stile = STATO_CONTROLLO_LABELS[row.stato_controllo] || STATO_CONTROLLO_LABELS.parziali;
                    const ess =
                      STATO_ESSENZIALE_LABELS[row.stato_essenziale] || STATO_ESSENZIALE_LABELS.da_verificare;
                    return (
                      <tr
                        key={row.richiesta_id}
                        className="align-top border-b border-slate-50 hover:bg-slate-50/40"
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            disabled={!row.sollecitabile}
                            checked={checked.has(row.richiesta_id)}
                            onChange={() => toggle(row.richiesta_id)}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-slate-900">
                            {row.ospite_cognome} {row.ospite_nome}
                          </div>
                          <div className="text-xs text-slate-400">
                            {(row.farmaci_richiesti || []).slice(0, 3).join(", ")}
                            {(row.farmaci_richiesti || []).length > 3 ? "…" : ""}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${ess.className}`}
                          >
                            {ess.label}
                          </span>
                          <div className="mt-0.5 text-[10px] text-slate-400">
                            {row.n_ricette || 0} ricett{(row.n_ricette || 0) === 1 ? "a" : "e"}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${stile.className}`}
                          >
                            {stile.label}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {(row.mancanti_in_ricette || []).length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Nessuno
                            </span>
                          ) : (
                            <ul className="space-y-0.5 text-xs text-rose-700">
                              {(row.mancanti_in_ricette || []).map((m) => (
                                <li key={m} className="flex items-start gap-1">
                                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                  {m}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-600">
                          {row.giorni_attesa}g
                          {row.scadenza_superata && (
                            <Badge
                              variant="outline"
                              className="ml-1 rounded-full text-[10px] text-rose-600"
                            >
                              scaduta
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-600">{row.solleciti_count || 0}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-lg"
                              disabled={loadingDetail}
                              onClick={() => openDetail(row.richiesta_id)}
                            >
                              <Eye className="mr-1 h-3.5 w-3.5" />
                              Controlla
                            </Button>
                            {row.sollecitabile && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={sending}
                                onClick={() => sollecitaUno(row.richiesta_id)}
                                className="rounded-lg border-amber-200 text-amber-800"
                              >
                                <Send className="mr-1 h-3.5 w-3.5" />
                                Sollecita
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Verifica ricette e copertura</h3>
                <p className="text-xs text-slate-500">
                  {(detail.richiesta?.ospite_cognome || "")} {(detail.richiesta?.ospite_nome || "")}
                </p>
                <Badge
                  variant="outline"
                  className={`mt-1 text-[10px] ${
                    (STATO_ESSENZIALE_LABELS[detail.stato_essenziale] || STATO_ESSENZIALE_LABELS.da_verificare)
                      .className
                  }`}
                >
                  {(STATO_ESSENZIALE_LABELS[detail.stato_essenziale] || STATO_ESSENZIALE_LABELS.da_verificare)
                    .label}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>
                Chiudi
              </Button>
            </div>

            <section className="mb-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Ricette (stesso file struttura)
              </h4>
              {(detail.ricette || []).length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                  Nessuna ricetta caricata dalla struttura.
                </p>
              ) : (
                <div className="space-y-2">
                  {(detail.ricette || []).map((ric) => (
                    <div key={ric.id} className="rounded-xl border border-slate-200 p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="font-medium text-slate-900">
                            Ricetta {(ric.created_at || "").slice(0, 10) || ric.id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(ric.farmaci_rilevati || []).length} farmaci rilevati
                            {ric.controllo_farmacia?.stato
                              ? ` · ${ric.controllo_farmacia.stato.replace(/_/g, " ")}`
                              : ""}
                          </p>
                        </div>
                        <select
                          className="h-8 rounded-lg border border-slate-200 px-2 text-xs"
                          value={ric.controllo_farmacia?.stato || "ricevuta"}
                          disabled={busy === `st-${ric.id}`}
                          onChange={(e) => setRicettaStato(ric.id, e.target.value)}
                        >
                          <option value="ricevuta">Ricevuta</option>
                          <option value="da_verificare">Da verificare</option>
                          <option value="illeggibile">Illeggibile</option>
                          <option value="da_integrare">Da integrare</option>
                        </select>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(ric.allegati || []).map((a) => (
                          <Button
                            key={a.id}
                            size="sm"
                            variant="outline"
                            className="rounded-lg"
                            disabled={busy === `dl-${a.id}`}
                            onClick={() => downloadAllegato(ric.id, a.id, a.file_name)}
                          >
                            <Download className="mr-1 h-3.5 w-3.5" />
                            {a.file_name || "Scarica"}
                          </Button>
                        ))}
                        {!(ric.allegati || []).length && (
                          <span className="text-xs text-slate-400">Nessun allegato</span>
                        )}
                      </div>
                      {(ric.farmaci_rilevati || []).length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                          {(ric.farmaci_rilevati || []).map((f) => (
                            <li key={f.id || f.farmaco}>
                              {f.farmaco}
                              {f.principio_attivo ? ` (${f.principio_attivo})` : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Farmaci richiesti — esito per farmaco
              </h4>
              <div className="space-y-2">
                {(detail.farmaci_richiesti || []).map((f) => {
                  const ricetteOpts = (detail.ricette || []).flatMap((ric) =>
                    (ric.farmaci_rilevati || []).map((rf) => ({
                      key: `${ric.id}::${rf.id || rf.farmaco}`,
                      ricettaId: ric.id,
                      farmacoId: rf.id,
                      label: `${(ric.created_at || "").slice(0, 10) || ric.id.slice(0, 8)} — ${rf.farmaco || "farmaco"}`,
                    }))
                  );
                  const assocKey =
                    f.associazione?.ricetta_id && f.associazione?.ricetta_farmaco_id
                      ? `${f.associazione.ricetta_id}::${f.associazione.ricetta_farmaco_id}`
                      : "";
                  return (
                    <div
                      key={f.id}
                      className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50/80 p-3"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900">{f.farmaco}</p>
                          {f.principio_attivo ? (
                            <p className="text-xs text-slate-500">{f.principio_attivo}</p>
                          ) : null}
                          {f.confezioni_richieste != null ? (
                            <p className="text-[11px] text-slate-400">
                              Confezioni richieste: {f.confezioni_richieste}
                            </p>
                          ) : null}
                        </div>
                        <select
                          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
                          value={f.copertura || "da_verificare"}
                          disabled={busy === `cov-${f.id}`}
                          onChange={(e) => {
                            const sel = ricetteOpts.find((o) => o.key === assocKey) || ricetteOpts[0];
                            setCopertura(f.id, e.target.value, sel?.ricettaId, sel?.farmacoId);
                          }}
                        >
                          {Object.entries(COPERTURA_FARMACO_LABELS).map(([id, label]) => (
                            <option key={id} value={id}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {ricetteOpts.length > 0 ? (
                        <div>
                          <label className="mb-1 block text-[11px] text-slate-500">
                            Associa a riga ricetta
                          </label>
                          <select
                            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm"
                            value={assocKey}
                            disabled={busy === `cov-${f.id}`}
                            onChange={(e) => {
                              const opt = ricetteOpts.find((o) => o.key === e.target.value);
                              setCopertura(
                                f.id,
                                f.copertura || "coperto",
                                opt?.ricettaId,
                                opt?.farmacoId
                              );
                            }}
                          >
                            <option value="">— Nessuna associazione —</option>
                            {ricetteOpts.map((o) => (
                              <option key={o.key} value={o.key}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {!(detail.farmaci_richiesti || []).length && (
                  <p className="text-sm text-slate-400">Nessun farmaco in richiesta</p>
                )}
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Azioni verso la struttura
              </h4>
              <textarea
                className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                rows={2}
                placeholder="Nota opzionale…"
                value={azioneMsg}
                onChange={(e) => setAzioneMsg(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  disabled={!!busy}
                  onClick={() => inviaAzione("nota")}
                >
                  <MessageSquare className="mr-1 h-3.5 w-3.5" />
                  Aggiungi nota
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  disabled={!!busy}
                  onClick={() => inviaAzione("nuova_ricetta")}
                >
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  Richiedi nuova ricetta
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg text-amber-800"
                  disabled={!!busy || !(detail.ricette || []).length}
                  onClick={() => inviaAzione("documento_illeggibile")}
                >
                  <FileWarning className="mr-1 h-3.5 w-3.5" />
                  Documento più leggibile
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                Le richieste arrivano in Deblistering → Comunicazioni. Non si creano account medici né
                link esterni.
              </p>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
